/**
 * The console's rejection-reason banner (SPEC §5, reject-v1) — the webapp
 * twin of the mobile SPA's banner. NOT a modal: a compact bar slides down
 * from the top of the viewport after a Reject lands, covers nothing but its
 * own strip, and auto-dismisses after ~7s. One click on a chip submits;
 * closing or ignoring it is itself an answer (dismissed / timeout).
 *
 * The outcome posts `/api/review/reject-reason` (console server → backend
 * gateway), and `backend` emits the `review_reject_reason` usage event
 * under THIS caller's identity — the console never talks to the event
 * pipeline directly.
 *
 * Sampling parity with mobile: p = 1.0, capped. The mobile cap is 3 per
 * walk; a console session reviews across walks, so the cap bucket here is
 * the item's visit when the queue knows it, else one shared session bucket.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { postRejectReason, type ConsoleItemKind } from "../api";
import { C, FONT_MONO } from "../tokens";

export const REJECT_PROMPT_P = 1.0;
export const REJECT_PROMPT_CAP = 3;

const shownByBucket = new Map<string, number>();

export interface RejectPromptPlan {
  p: number;
  shown: boolean;
}

/** Call once per rejection, at the click. Mirrors the SPA's contract: when
 *  it answers shown=true the caller MUST present the banner. */
export function planRejectReasonPrompt(visitId: string | null | undefined): RejectPromptPlan {
  const key = visitId || "__console_session__";
  const used = shownByBucket.get(key) ?? 0;
  const shown = used < REJECT_PROMPT_CAP && Math.random() < REJECT_PROMPT_P;
  if (shown) shownByBucket.set(key, used + 1);
  return { p: REJECT_PROMPT_P, shown };
}

export interface ConsoleRejectPrompt {
  itemId: string;
  kind: ConsoleItemKind;
  /** The workspace query string every console api call carries. */
  search: string;
  jobSiteId?: string;
  visitId?: string;
  promptKey: number;
}

const PROMPT_EVENT = "ledai-console:rejectReasonPrompt";

/** Fire-and-forget from the decision control; the host lives in Shell. */
export function showRejectReasonPrompt(prompt: Omit<ConsoleRejectPrompt, "promptKey">): void {
  try {
    window.dispatchEvent(
      new CustomEvent(PROMPT_EVENT, { detail: { ...prompt, promptKey: Date.now() } }),
    );
  } catch {
    // a telemetry prompt must never break the review flow
  }
}

type RejectReasonLabel = "not_said" | "not_worth_logging" | "already_covered" | "other";

/** Emit-once per promptKey at module scope — a replaced banner still reports
 *  `dismissed`, so every shown prompt produces exactly one outcome row. */
const emittedKeys = new Set<number>();
function emitOutcome(
  prompt: ConsoleRejectPrompt,
  response: "selected" | "dismissed" | "timeout",
  reason: RejectReasonLabel | null,
  freeText?: string,
): void {
  if (emittedKeys.has(prompt.promptKey)) return;
  emittedKeys.add(prompt.promptKey);
  void postRejectReason(prompt.search, {
    itemId: prompt.itemId,
    kind: prompt.kind,
    response,
    reason,
    ...(freeText?.trim() ? { freeText: freeText.trim().slice(0, 500) } : {}),
    ...(prompt.jobSiteId ? { jobSiteId: prompt.jobSiteId } : {}),
    ...(prompt.visitId ? { visitId: prompt.visitId } : {}),
  });
}

const AUTO_DISMISS_MS = 7_000;
const OTHER_EXTENSION_MS = 20_000;
const EXIT_MS = 160;

const CHIPS: Array<{ reason: RejectReasonLabel; label: string }> = [
  { reason: "not_said", label: "Didn’t talk about this" },
  { reason: "not_worth_logging", label: "Not worth logging" },
  { reason: "already_covered", label: "Duplicate" },
  { reason: "other", label: "Other…" },
];

function Banner({ prompt, onDone }: { prompt: ConsoleRejectPrompt; onDone: () => void }) {
  const [entered, setEntered] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [otherOpen, setOtherOpen] = useState(false);
  const [otherText, setOtherText] = useState("");
  const doneRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const finish = useCallback(
    (response: "selected" | "dismissed" | "timeout", reason: RejectReasonLabel | null, freeText?: string) => {
      if (doneRef.current) return;
      doneRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      emitOutcome(prompt, response, reason, freeText);
      setLeaving(true);
      setTimeout(onDone, EXIT_MS);
    },
    [onDone, prompt],
  );

  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    timerRef.current = setTimeout(() => finish("timeout", null), AUTO_DISMISS_MS);
    return () => {
      cancelAnimationFrame(raf);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompt.promptKey]);

  const chip: React.CSSProperties = {
    height: 28,
    padding: "0 12px",
    borderRadius: 999,
    border: `1px solid ${C.ink}22`,
    background: C.surface,
    color: C.ink,
    fontSize: 12.8,
    fontWeight: 600,
    cursor: "pointer",
  };

  return (
    <div
      data-testid="console-reject-reason-banner"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 300,
        display: "flex",
        justifyContent: "center",
        padding: "10px 12px 0",
        pointerEvents: "none",
        transform: entered && !leaving ? "translateY(0)" : "translateY(-120%)",
        transition: `transform ${entered ? 240 : EXIT_MS}ms cubic-bezier(0.2, 0.8, 0.2, 1)`,
      }}
    >
      <div
        style={{
          pointerEvents: leaving ? "none" : "auto",
          display: "flex",
          alignItems: "center",
          gap: 10,
          maxWidth: 720,
          borderRadius: 12,
          border: `1px solid ${C.ink}1f`,
          background: C.faint,
          boxShadow: "0 10px 28px rgba(20, 17, 12, 0.16)",
          padding: "9px 12px",
        }}
      >
        <span
          style={{
            fontFamily: FONT_MONO,
            fontSize: 11.5,
            letterSpacing: "0.4px",
            textTransform: "uppercase",
            color: C.sub,
            whiteSpace: "nowrap",
          }}
        >
          Why skip this one?
        </span>
        {!otherOpen ? (
          <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {CHIPS.map((c) => (
              <button
                key={c.reason}
                type="button"
                data-testid={`console-reject-reason-chip-${c.reason}`}
                style={chip}
                onClick={() => {
                  if (c.reason === "other") {
                    setOtherOpen(true);
                    if (timerRef.current) clearTimeout(timerRef.current);
                    timerRef.current = setTimeout(() => finish("timeout", null), OTHER_EXTENSION_MS);
                  } else {
                    finish("selected", c.reason);
                  }
                }}
              >
                {c.label}
              </button>
            ))}
          </span>
        ) : (
          <span style={{ display: "flex", gap: 6, flex: 1, minWidth: 220 }}>
            <input
              autoFocus
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && otherText.trim()) finish("selected", "other", otherText);
                if (e.key === "Escape") finish("dismissed", null);
              }}
              placeholder="What was off about it?"
              data-testid="console-reject-reason-other-input"
              style={{
                flex: 1,
                minWidth: 0,
                height: 28,
                borderRadius: 8,
                border: `1px solid ${C.ink}22`,
                background: C.surface,
                color: C.ink,
                fontSize: 12.8,
                padding: "0 9px",
              }}
            />
            <button
              type="button"
              disabled={!otherText.trim()}
              data-testid="console-reject-reason-other-send"
              style={{ ...chip, opacity: otherText.trim() ? 1 : 0.5 }}
              onClick={() => finish("selected", "other", otherText)}
            >
              Send
            </button>
          </span>
        )}
        <button
          type="button"
          aria-label="Dismiss"
          data-testid="console-reject-reason-dismiss"
          onClick={() => finish("dismissed", null)}
          style={{
            border: "none",
            background: "transparent",
            color: C.sub2,
            fontSize: 15,
            lineHeight: 1,
            cursor: "pointer",
            padding: "2px 2px",
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}

/** Mounted ONCE in the console shell. */
export function RejectReasonBannerHost() {
  const [prompt, setPrompt] = useState<ConsoleRejectPrompt | null>(null);
  const currentRef = useRef<ConsoleRejectPrompt | null>(null);
  useEffect(() => {
    const onPrompt = (e: Event) => {
      const detail = (e as CustomEvent).detail as ConsoleRejectPrompt | undefined;
      if (!detail || typeof detail.itemId !== "string") return;
      const prev = currentRef.current;
      if (prev && prev.promptKey !== detail.promptKey) emitOutcome(prev, "dismissed", null);
      currentRef.current = detail;
      setPrompt(detail);
    };
    window.addEventListener(PROMPT_EVENT, onPrompt);
    return () => window.removeEventListener(PROMPT_EVENT, onPrompt);
  }, []);
  if (!prompt) return null;
  return (
    <Banner
      key={prompt.promptKey}
      prompt={prompt}
      onDone={() => {
        if (currentRef.current?.promptKey === prompt.promptKey) currentRef.current = null;
        setPrompt((cur) => (cur?.promptKey === prompt.promptKey ? null : cur));
      }}
    />
  );
}
